import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  StringField,
  TextField,
  IntegerField,
  DecimalField,
  JsonField,
  TimestampField,
} from '@ayocore/platform/common/helpers/schema-types';
import dealSchemaDefinition from './deal.schema';
import dealVersionSchemaDefinition from './deal-version.schema';
import partnerSchemaDefinition from './partner.schema';
import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';

import { name as moduleName } from '../module.json';
/**
 * Строки сделки — позиции товаров/услуг.
 */
const DealRowSchema = Type.Object({
  ...SystemFields,

  deal_id: RelationField(dealSchemaDefinition, 'ID сделки', {
    description: 'Сделка, к которой относится позиция',
    relationName: 'deal',
  }),

  deal_version_id: RelationField(dealVersionSchemaDefinition, 'ID версии сделки', {
    description: 'Версия подборки строк, к которой относится позиция',
    relationName: 'deal_version',
  }),

  product_id: IntegerField({
    title: 'ID товара',
    nullable: true,
    minimum: 0,
    description: 'ID товара из справочника (необязательно)',
  }),

  product_name: StringField({
    title: 'Наименование',
    minLength: 1,
    maxLength: 500,
    description: 'Наименование товара/услуги',
  }),

  product_category: StringField({
    title: 'Категория',
    nullable: true,
    maxLength: 255,
    description: 'Категория товара/услуги',
  }),

  product_brand: StringField({
    title: 'Бренд',
    nullable: true,
    maxLength: 255,
    description: 'Бренд/производитель товара',
  }),

  product_supplier: StringField({
    title: 'Поставщик (текст)',
    nullable: true,
    maxLength: 255,
    description: 'Текстовое название поставщика (для быстрого ввода на этапе черновика)',
  }),

  /** Партнёр-поставщик из справочника. Обязателен для создания заказа на закупку. */
  supplier: RelationOne(partnerSchemaDefinition),
  supplier_id: RelationField(partnerSchemaDefinition, 'ID поставщика', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Партнёр-поставщик из справочника (обязателен для создания Purchase Order)',
    relationName: 'supplier',
  }),

  quantity: DecimalField({
    title: 'Количество',
    minimum: 0,
    precision: 10,
    scale: 3,
    description: 'Количество товара/услуги',
  }),

  price: DecimalField({
    title: 'Цена',
    minimum: 0,
    precision: 15,
    scale: 2,
    description: 'Цена за единицу',
  }),

  amount: DecimalField({
    title: 'Сумма',
    minimum: 0,
    precision: 15,
    scale: 2,
    description: 'Общая сумма (количество × цена)',
  }),

  vat: DecimalField({
    title: 'НДС',
    minimum: 0,
    maximum: 100,
    precision: 5,
    scale: 2,
    default: 0,
    description: 'Налог на добавленную стоимость (%)',
  }),

  purchase_price: DecimalField({
    title: 'Закупочная цена',
    nullable: true,
    minimum: 0,
    precision: 15,
    scale: 2,
    description: 'Закупочная цена за единицу',
  }),

  expenses_amount: DecimalField({
    title: 'Сумма расходов',
    nullable: true,
    minimum: 0,
    precision: 15,
    scale: 2,
    description: 'Рассчитанная сумма расходов: закупочная цена × кол-во + сумма услуг подрядчиков',
  }),

  metadata: JsonField(Type.Any(), {
    title: 'Метаданные',
    nullable: true,
    description: 'Дополнительные данные по позиции (например, ссылки на изображения товара)',
  }),

  notes: TextField({
    title: 'Примечание',
    nullable: true,
    description: 'Заметки по позиции сделки',
  }),

  /** Согласование позиции. */
  approved_at: TimestampField({
    title: 'Дата согласования',
    nullable: true,
    description: 'Когда позиция была согласована',
  }),

  approved_by: RelationOne(userSchemaDefinition),
  approved_by_id: RelationField(userSchemaDefinition, 'Кем согласовано', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Пользователь, согласовавший позицию',
    relationName: 'approved_by',
  }),
});

export type DealRow = Static<typeof DealRowSchema>;

export type CreateDealRow = Omit<
  DealRow,
  'id' | 'uuid' | 'created_at' | 'updated_at' | 'sort_order' | 'supplier' | 'approved_by'
>;

export type UpdateDealRow = Partial<CreateDealRow>;

export default defineSchema({
  moduleName,
  tableName: 'deal_rows', // System will prefix with 'crm_'
  schema: DealRowSchema,
  additionalIndexes: [
    'deal_id',
    'deal_version_id',
    'product_id',
    'product_name',
    'product_category',
    'supplier_id',
  ],
  foreignKeys: [],
  dependencies: ['crm_deals', 'crm_deal_versions', 'crm_partners', 'identity_users'],
});
